import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { useTheme } from '../contexts/ThemeContext';
import PasswordResetService from '../services/passwordResetService';
import './ForgotPassword.css';

const OTP_VALIDITY_SECONDS = 180;
const RESEND_COOLDOWN_SECONDS = 30;
const OTP_SENT_AT_KEY = 'forgot_password_otp_sent_at';
const OTP_EMAIL_KEY = 'forgot_password_email';

const isValidEmail = (email) => {
  const v = String(email || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

const formatCountdown = (seconds) => {
  const s = Math.max(0, seconds);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const passwordRules = (password) => {
  const v = String(password || '');
  return {
    minLen: v.length >= 8,
    upper: /[A-Z]/.test(v),
    lower: /[a-z]/.test(v),
    number: /\d/.test(v),
    special: /[^A-Za-z0-9]/.test(v)
  };
};

const passwordStrength = (password) => {
  const rules = passwordRules(password);
  const score = Object.values(rules).filter(Boolean).length;
  if (score <= 2) return { label: 'Weak', score, tone: 'weak' };
  if (score === 3 || score === 4) return { label: 'Medium', score, tone: 'medium' };
  return { label: 'Strong', score, tone: 'strong' };
};

const getRemainingSeconds = () => {
  const raw = localStorage.getItem(OTP_SENT_AT_KEY);
  if (!raw) return 0;
  const sentAt = parseInt(raw, 10);
  if (!Number.isFinite(sentAt)) return 0;
  const elapsed = Math.floor((Date.now() - sentAt) / 1000);
  return Math.max(0, OTP_VALIDITY_SECONDS - elapsed);
};

const ForgotPassword = ({ onBackToLogin }) => {
  // const { theme } = useTheme();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const otpRefs = useRef([]);

  const otpValue = useMemo(() => otpDigits.join(''), [otpDigits]);
  const isExpired = remainingSeconds <= 0 && step === 2;

  useEffect(() => {
    const savedEmail = localStorage.getItem(OTP_EMAIL_KEY);
    if (savedEmail) setEmail(savedEmail);

    const remaining = getRemainingSeconds();
    if (remaining > 0) {
      setRemainingSeconds(remaining);
      setStep(2);
    }
  }, []);

  useEffect(() => {
    if (step !== 2) return;

    const tick = () => setRemainingSeconds(getRemainingSeconds());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (step !== 2) return;
    if (resendCooldown <= 0) return;

    const id = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(id);
  }, [resendCooldown, step]);

  useEffect(() => {
    if (step === 2 && remainingSeconds <= 0) {
      setInfo('OTP expired. Please request a new OTP.');
    }
  }, [remainingSeconds, step]);

  useEffect(() => {
    if (!showSuccess) return;
    const id = setTimeout(() => {
      onBackToLogin();
    }, 3500);
    return () => clearTimeout(id);
  }, [showSuccess, onBackToLogin]);

  const canSendOtp = useMemo(() => {
    return isValidEmail(email) && !isLoading;
  }, [email, isLoading]);

  const strength = useMemo(() => passwordStrength(newPassword), [newPassword]);
  const rules = useMemo(() => passwordRules(newPassword), [newPassword]);

  const canResetPassword = useMemo(() => {
    const otpOk = /^\d{6}$/.test(String(otpValue || '').trim());
    const passOk = Object.values(passwordRules(newPassword)).every(Boolean);
    const confirmOk = newPassword === confirmPassword && confirmPassword.length > 0;
    return otpOk && passOk && confirmOk && !isExpired && !isLoading;
  }, [otpValue, newPassword, confirmPassword, isExpired, isLoading]);

  const startOtpTimer = (emailToStore) => {
    localStorage.setItem(OTP_SENT_AT_KEY, String(Date.now()));
    localStorage.setItem(OTP_EMAIL_KEY, emailToStore);
    setRemainingSeconds(OTP_VALIDITY_SECONDS);
  };

  const clearOtpTimer = () => {
    localStorage.removeItem(OTP_SENT_AT_KEY);
    localStorage.removeItem(OTP_EMAIL_KEY);
    setRemainingSeconds(0);
  };

  const handleSendOtp = async (e, forceEmail) => {
    e?.preventDefault?.();
    const targetEmail = String(forceEmail ?? email).trim();

    setError('');
    setInfo('');

    if (!isValidEmail(targetEmail)) {
      setError('Please enter a valid email.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await PasswordResetService.sendResetOtp(targetEmail);
      if (res?.success) {
        const message = res?.data?.message || 'OTP sent to email';
        setInfo(message);
        setEmail(targetEmail);
        setOtpDigits(['', '', '', '', '', '']);
        startOtpTimer(targetEmail);
        setStep(2);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setTimeout(() => {
          otpRefs.current?.[0]?.focus?.();
        }, 50);
      } else {
        setError(res?.message || 'Failed to send OTP.');
      }
    } catch {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const setOtpAt = (index, digit) => {
    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
  };

  const handleOtpChange = (index, value) => {
    const v = String(value || '').replace(/\D/g, '');
    if (!v) {
      setOtpAt(index, '');
      return;
    }

    const digit = v.slice(-1);
    setOtpAt(index, digit);
    if (index < 5) {
      otpRefs.current?.[index + 1]?.focus?.();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key !== 'Backspace') return;
    if (otpDigits[index]) {
      setOtpAt(index, '');
      return;
    }
    if (index > 0) {
      otpRefs.current?.[index - 1]?.focus?.();
      setOtpAt(index - 1, '');
    }
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData?.getData('text') || '';
    const digits = text.replace(/\D/g, '').slice(0, 6).split('');
    if (digits.length === 0) return;
    e.preventDefault();
    setOtpDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < 6; i += 1) {
        next[i] = digits[i] || '';
      }
      return next;
    });

    const focusIndex = Math.min(digits.length, 6) - 1;
    setTimeout(() => otpRefs.current?.[focusIndex]?.focus?.(), 0);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    setError('');
    setInfo('');

    const targetEmail = String(email || '').trim();

    if (!isValidEmail(targetEmail)) {
      setError('Please enter a valid email.');
      return;
    }

    if (!/^\d{6}$/.test(String(otpValue || '').trim())) {
      setError('OTP must be exactly 6 digits.');
      return;
    }

    if (!Object.values(passwordRules(newPassword)).every(Boolean)) {
      setError('Please enter a stronger password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Confirm password must match new password.');
      return;
    }

    if (getRemainingSeconds() <= 0) {
      setError('OTP expired. Please request a new OTP.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await PasswordResetService.verifyResetOtp({
        email: targetEmail,
        otp: String(otpValue).trim(),
        new_password: newPassword
      });

      if (res?.success) {
        setError('');
        setInfo('');
        setShowSuccess(true);
        clearOtpTimer();
      } else {
        setError(res?.message || 'Failed to reset password.');
      }
    } catch {
      setError('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-v2 light">
      <div className="forgot-v2__bg" />
      <div className="forgot-v2__wrap">
        <main className={`forgot-v2__card ${showSuccess ? 'is-success' : ''}`} role="main">
          <div className="forgot-v2__brand">
            <div className="forgot-v2__logo" aria-hidden="true">QZ</div>
          </div>

          {!showSuccess ? (
            <>
              <header className="forgot-v2__header">
                <h1>Forgot Password</h1>
                <p>
                  {step === 1
                    ? "Enter your email address and we’ll send you a verification code."
                    : 'Enter the code and choose a new password.'}
                </p>
              </header>

              <div className="forgot-v2__steps" aria-label="Progress">
                <div className={`step ${step === 1 ? 'active' : 'done'}`}>
                  <div className="dot">1</div>
                  <div className="label">Verify Email</div>
                </div>
                <div className="bar" />
                <div className={`step ${step === 2 ? 'active' : ''}`}>
                  <div className="dot">2</div>
                  <div className="label">OTP & New Password</div>
                </div>
              </div>

              {error && (
                <div className="forgot-v2__alert" role="alert">
                  {error}
                </div>
              )}

              {info && (
                <div className="forgot-v2__info" role="status">
                  {info}
                </div>
              )}

              <div className={`forgot-v2__content ${step === 1 ? 'step-1' : 'step-2'}`}>
                {step === 1 ? (
                  <form onSubmit={handleSendOtp} className="forgot-v2__form">
                    <div className="field">
                      <label htmlFor="forgot-email">Email</label>
                      <div className="input-icon">
                        <span className="icon" aria-hidden="true">@</span>
                        <input
                          id="forgot-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@example.com"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <button type="submit" className="btn-primary" disabled={!canSendOtp}>
                      {isLoading ? (
                        <span className="btn-loading">
                          <span className="spinner" />
                          Sending...
                        </span>
                      ) : (
                        'Send Verification Code'
                      )}
                    </button>

                    <button type="button" className="btn-link" onClick={onBackToLogin}>
                      Back to Login
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword} className="forgot-v2__form">
                    <div className="meta">
                      <div className={`countdown ${remainingSeconds > 0 ? '' : 'expired'}`}>
                        {remainingSeconds > 0
                          ? `Code expires in ${formatCountdown(remainingSeconds)}`
                          : 'OTP expired. Please request a new OTP.'}
                      </div>
                    </div>

                    <div className="field">
                      <label>Email</label>
                      <div className="input-icon">
                        <span className="icon" aria-hidden="true">@</span>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@example.com"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="field">
                      <label>Verification code</label>
                      <div className="otp" onPaste={handleOtpPaste}>
                        {otpDigits.map((d, i) => (
                          <input
                            key={i}
                            ref={(el) => {
                              otpRefs.current[i] = el;
                            }}
                            inputMode="numeric"
                            className="otp-box"
                            value={d}
                            onChange={(e) => handleOtpChange(i, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                            disabled={isLoading}
                            aria-label={`OTP digit ${i + 1}`}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="field">
                      <label>New Password</label>
                      <div className="input-action">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="New password"
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => setShowNewPassword((p) => !p)}
                          disabled={isLoading}
                        >
                          {showNewPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      <div className={`strength ${strength.tone}`} aria-label="Password strength">
                        <div className="bar">
                          <div className="fill" style={{ width: `${(strength.score / 5) * 100}%` }} />
                        </div>
                        <div className="text">{strength.label}</div>
                      </div>

                      <ul className="checklist" aria-label="Password rules">
                        <li className={rules.minLen ? 'ok' : ''}>At least 8 characters</li>
                        <li className={rules.upper ? 'ok' : ''}>Uppercase letter</li>
                        <li className={rules.lower ? 'ok' : ''}>Lowercase letter</li>
                        <li className={rules.number ? 'ok' : ''}>Number</li>
                        <li className={rules.special ? 'ok' : ''}>Special character</li>
                      </ul>
                    </div>

                    <div className="field">
                      <label>Confirm Password</label>
                      <div className="input-action">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm password"
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => setShowConfirmPassword((p) => !p)}
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>

                    <button type="submit" className="btn-primary" disabled={!canResetPassword}>
                      {isLoading ? (
                        <span className="btn-loading">
                          <span className="spinner" />
                          Resetting...
                        </span>
                      ) : (
                        'Reset Password'
                      )}
                    </button>

                    {remainingSeconds <= 0 && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={(e) => {
                          if (resendCooldown > 0) return;
                          setResendCooldown(RESEND_COOLDOWN_SECONDS);
                          handleSendOtp(e, email);
                        }}
                        disabled={!isValidEmail(email) || isLoading || resendCooldown > 0}
                      >
                        {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
                      </button>
                    )}

                    <button type="button" className="btn-link" onClick={onBackToLogin}>
                      Back to Login
                    </button>
                  </form>
                )}
              </div>
            </>
          ) : (
            <div className="forgot-v2__success" role="status">
              <div className="success-icon" aria-hidden="true">✓</div>
              <h2>Password Reset Successful</h2>
              <p>You can now log in with your new password.</p>
              <button type="button" className="btn-primary" onClick={onBackToLogin}>
                Go to Login
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ForgotPassword;
